// Speak the assistant's replies aloud (ElevenLabs) — Simone's voice for the
// TYPED chat surface. The whole TTS chain already exists (server
// /core/ai/voice/tts → sdk.voiceTts → streamed mpeg); this module is the one
// wire that was missing: same conversation pipeline writes the text, this
// speaks it. Playback mirrors documents' readerVoice (blob → object URL →
// one scope-held Audio element), including the degrade-to-silence rule: the
// text is already on screen, so a failed play NEVER surfaces an error and
// NEVER falls back to a browser voice.
//
// Frank constraint: no module-scope helper reads from exported fns — helpers
// are local consts inside each function; cross-file access via el.call.

// Default OFF (S3, tickets/sonnet.md, Nika 2026-08-16: "if voice is NOT
// activated, do not read the AI response aloud by default" — reverses the
// prior default-ON decision this comment used to document). A fresh
// profile with no stored preference must produce no speech; the
// AiSettingsPanel toggle turns it on per user (localStorage), and every
// failure path is silent.
export const speakRepliesEnabled = function () {
  try {
    const v = window.localStorage.getItem('symbols_ai_speak_replies')
    return v === 'on'
  } catch (_) {
    return false
  }
}

export const setSpeakReplies = function (on) {
  try {
    window.localStorage.setItem('symbols_ai_speak_replies', on ? 'on' : 'off')
  } catch (_) {}
  const el = this
  const root = el && el.getRoot ? el.getRoot() : null
  if (root && root.update) root.update({ _speakReplies: !!on }, { preventFetch: true })
}

// Create + gesture-play the ONE audio element later replies reuse. Must run
// inside a user gesture (composer send, settings toggle) — Chromium/Safari
// autoplay policy blesses an element that already played, which is what lets
// a reply that lands 5-30s after the click still speak. 44-byte silent WAV.
export const primeSpeakAudio = function () {
  const el = this
  if (!el || !el.scope) return
  const h = el.scope.speakReply || (el.scope.speakReply = {})
  if (h.audio) return
  try {
    const silent =
      'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA='
    const audio = new Audio(silent)
    audio.volume = 1
    const p = audio.play()
    if (p && typeof p.catch === 'function') p.catch(() => {})
    try {
      audio.pause()
    } catch (_) {}
    h.audio = audio
  } catch (_) {}
}

export const stopSpeakReply = function () {
  const el = this
  const h = el && el.scope ? el.scope.speakReply || (el.scope.speakReply = {}) : {}
  h.token = null
  if (h.audio) {
    try {
      h.audio.pause()
    } catch (_) {}
    h.audio.onended = null
    h.audio.onpause = null
  }
  if (h.url) {
    try {
      URL.revokeObjectURL(h.url)
    } catch (_) {}
    h.url = null
  }
  const root = el && el.getRoot ? el.getRoot() : null
  if (root && root.update && root.voiceStatus === 'speaking') {
    root.update({ voiceStatus: null }, { preventFetch: true })
  }
}

// speakReply(text) — strip the markdown down to speakable prose, fetch the
// ElevenLabs stream through the SDK, play it on the scope-held element.
// Guards: enabled pref, sdk capability, live voice session (it speaks for
// itself — never double-voice), hidden tab. Supersession via token: a newer
// reply silently replaces a still-playing older one.
export const speakReply = async function (text, opts) {
  const el = this
  const root = el && el.getRoot ? el.getRoot() : null
  if (!root) return { ok: false }
  if (!el.call('speakRepliesEnabled')) return { ok: false, off: true }
  if (root.voiceOpen) return { ok: false, voiceSessionOwnsAudio: true }
  try {
    if (typeof document !== 'undefined' && document.hidden) return { ok: false, hidden: true }
  } catch (_) {}
  const sdk =
    (el && (el.sdk || (el.context && el.context.methods && el.context.methods.sdk))) || null
  if (!sdk || typeof sdk.voiceTts !== 'function') return { ok: false }

  // Markdown → speakable prose (same shape voiceSession.speakable uses):
  // drop fences whole, unwrap inline code/links/emphasis/headings, collapse
  // whitespace, cap the length so a long answer reads its gist, not a saga.
  const speakable = (md) => {
    let t = String(md || '')
    t = t.replace(/```[\s\S]*?```/g, ' ')
    t = t.replace(/`([^`]*)`/g, '$1')
    t = t.replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    t = t.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    t = t.replace(/^#{1,6}\s+/gm, '')
    t = t.replace(/[*_>#|]/g, ' ')
    t = t.replace(/\s+/g, ' ').trim()
    return t.length > 700 ? `${t.slice(0, 700)}…` : t
  }
  const prose = speakable(text)
  if (!prose) return { ok: false }

  const h = el.scope ? el.scope.speakReply || (el.scope.speakReply = {}) : {}
  const token = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
  h.token = token
  el.call('stopSpeakReply')
  h.token = token

  try {
    // The workspace's own voice (functions/workspaceVoice.js — the admin's
    // pick on /admin/workspace-settings) unless the caller names one; none =
    // the server's default voice.
    const voiceId = (opts && opts.voiceId) || el.call('workspaceVoiceId', root) || undefined
    const res = await sdk.voiceTts({ text: prose, voiceId })
    if (!res || !res.ok || h.token !== token) return { ok: false }
    const blob = await res.blob()
    if (h.token !== token) return { ok: false }
    const url = URL.createObjectURL(blob)
    h.url = url
    const audio = h.audio || new Audio()
    h.audio = audio
    audio.src = url
    audio.onended = () => {
      if (h.token === token) el.call('stopSpeakReply')
    }
    if (root.update) root.update({ voiceStatus: 'speaking' }, { preventFetch: true })
    const p = audio.play()
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // Autoplay refusal / decode failure — silent degrade, text is on
        // screen. Clear the status so the pill never sticks on "Speaking…".
        if (h.token === token) el.call('stopSpeakReply')
      })
    }
    return { ok: true }
  } catch (_) {
    if (h.token === token) el.call('stopSpeakReply')
    return { ok: false }
  }
}
